import { Injectable, inject } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { createEffect } from 'effector'

import { lastValueFrom, map, take } from 'rxjs'

import {
  BpmnMouseEvent,
  SwimlaneMenuMoveEvent,
  SwimlaneRenameEvent,
} from '@flowx/designer/modules/bpmn-editor/bpmn-editor.types'
import {
  NodeDefinition,
  NodeDefinitionNormalized,
  NodeDefinitionPatchApi,
  ProcessDefinition,
} from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import {
  AvailableViews,
  PanelSize,
  ProcessDetailsHelperService,
} from '@flowx/designer/modules/process-details/process-details-helper.service'
import { NodeConfigTabs } from '@flowx/designer/modules/process/components/node-details/node-details.types'
import { BoundaryEvent } from '@flowx/designer/shared/entities/boundary-event/boundary-event.types'
import { AreaPlatform } from '@flowx/designer/shared/entities/nav-area.entity'
import { FlxNavigationService } from '@flowx/designer/shared/services/navigation.service'
import { SwimlaneService } from '@flowx/designer/shared/services/swimlane.service'
import * as NodeActions from '@flowx/designer/shared/store/actions/node.actions'
import { selectDataModelEntitiesLeaves } from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { selectQueryParam } from '@flowx/designer/shared/store/selectors/router.selectors'
import { areaPlatformToPlatform } from '@flowx/designer/shared/utils/template-config.utils'

@Injectable({ providedIn: 'root' })
export class BpmnEditorEffects {
  readonly #swimlaneService = inject(SwimlaneService)
  readonly #router = inject(Router)
  readonly #route = inject(ActivatedRoute)
  readonly #store = inject(Store)
  readonly #processDetailsHelperService = inject(ProcessDetailsHelperService)
  readonly #navigationService = inject(FlxNavigationService)

  readonly toggleReadonlyFx = createEffect<NodeDefinitionNormalized, void>('toggleReadonlyFx').use(
    (node) => {
      this.#store.dispatch(
        NodeActions.updateNode({
          processVersionId: node.processId,
          node: node as unknown as NodeDefinitionPatchApi,
        })
      )
    }
  )

  readonly navigateToUIDesignerFx = createEffect<
    {
      navigationAreaId: number
      nodeId?: number
      swimlaneId?: string
    },
    void
  >('navigateToUIDesignerFx').use(({ navigationAreaId, nodeId, swimlaneId }) => {
    this.#navigationService.navigateToProcessNavigationArea(navigationAreaId, {
      queryParamsHandling: 'merge',
      queryParams: {
        nodeId: nodeId ?? null,
        boundaryEventId: null,
        swimlaneId,
        pivotAreaId: null,
        templateIds: null,
        platform: areaPlatformToPlatform(
          (
            this.#store.selectSignal(selectQueryParam('platform'))() as string
          )?.toUpperCase() as AreaPlatform
        ),
        filterReadonly: null,
      },
    })
  })

  readonly updateUrlParamsFx = createEffect('updateUrlParamsFx').use(
    ({ toSet, toDelete }: { toSet: Record<string, number | any>; toDelete: string[] }) => {
      this.updateUrlParams({ toSet, toDelete })
    }
  )

  readonly panelSizeFx = createEffect('panelSizeFx').use((size: PanelSize) => {
    this.#processDetailsHelperService.setPanelSize(size)
  })

  // NODE ACTIONS??

  readonly openConfigPanelFx = createEffect<
    { id: NodeDefinition['id'] | BoundaryEvent['id'] },
    void
  >('openConfigPanelFx').use((entity) => {
    this.updateUrlParams({ toSet: { nodeId: entity.id, nodePanelOpened: true }, toDelete: [] })
    this.#processDetailsHelperService.setSelectedView(AvailableViews.NODE_DETAILS)
  })

  readonly changeConfigPanelTabFx = createEffect<NodeConfigTabs, void>('changeConfigPanelTabFx')

  readonly changeConfigTabActionHandlerFx = createEffect<string, void>(
    'changeConfigTabActionHandlerFx'
  )

  readonly boundaryEventRemoveFx = createEffect('boundaryEventRemoveFx').use(
    (boundaryEvent: BoundaryEvent) => {
      this.#store.dispatch(
        NodeActions.deleteBoundaryEvent({
          boundaryEvent,
        })
      )
    }
  )

  // SWIMLANES- create separate file for swimlane effects

  readonly swimlaneRemoveFx = createEffect<BpmnMouseEvent<SwimlaneSummary>, void>(
    'swimlaneRemoveFx'
  ).use((selectedSwimlane) =>
    lastValueFrom(this.#swimlaneService.deleteSwimlane(selectedSwimlane.entity.id))
  )

  readonly swimlaneMoveFx = createEffect<
    { selectedSwimlane: BpmnMouseEvent<SwimlaneSummary>; event: SwimlaneMenuMoveEvent },
    void
  >('swimlaneMoveFx').use(({ selectedSwimlane: { entity }, event }) =>
    lastValueFrom(this.#swimlaneService.moveSwimlane(entity, event))
  )

  readonly swimlaneRenameFx = createEffect<
    SwimlaneRenameEvent,
    Pick<SwimlaneSummary, 'id' | 'name'>
  >('swimlaneRenameFx').use(({ swimlane, newName }: SwimlaneRenameEvent) =>
    lastValueFrom(
      this.#swimlaneService.updateSwimlaneName({
        ...swimlane,
        name: newName,
      })
    )
  )

  // NODE AI
  readonly checkProcessDataModelFx = createEffect<ProcessDefinition['id'], boolean>(
    'checkProcessDataModelFx'
  ).use(() =>
    lastValueFrom(
      // `select` never completes, so take the current value or the effect stays in flight
      // forever and pins the `loadingDataModel` flag that gates the AI actions sub-panel.
      this.#store.select(selectDataModelEntitiesLeaves).pipe(
        map((data) => !!data.length),
        take(1)
      )
    )
  )

  // HELPERS
  // TODO Figure out where this should go

  private updateUrlParams({
    toSet,
    toDelete,
  }: {
    toSet: Record<string, number | any>
    toDelete: string[]
  }): void {
    const queryParams = { ...this.#route.snapshot.queryParams }
    Object.entries(toSet).forEach(([key, value]) => {
      queryParams[key] = value
    })
    toDelete.forEach((key) => {
      delete queryParams[key]
    })

    this.#router.navigate([], {
      queryParams,
      relativeTo: this.#route,
      fragment: this.#route.snapshot.fragment,
    })
  }
}
